// file: client/src/offline/offlineStore.js
/**
 * MODULE 09 — offline state (Zustand). The single UI-facing source of truth:
 * connectivity, enrollment, outbox depth, sync progress, cache age.
 * Device identity/secret persist in localStorage (per-terminal, like tokens).
 */
import { create } from "zustand";

const LS_DEVICE = "pharmakon_sync_device";
const LS_CFG = "pharmakon_sync_cfg"; // last-known {enabled, config}

export const loadDevice = () => {
  try {
    return JSON.parse(localStorage.getItem(LS_DEVICE) || "null");
  } catch {
    return null;
  }
};
export const saveDevice = (d) =>
  localStorage.setItem(LS_DEVICE, JSON.stringify(d));
export const clearDevice = () => localStorage.removeItem(LS_DEVICE);

// The server flag is persisted so an OFFLINE RELOAD (where the /sync/status
// probe can't reach the server) still knows the module is active and serves
// the local database — instead of going inert and erroring every page.
const loadCfg = () => {
  try {
    return JSON.parse(localStorage.getItem(LS_CFG) || "null") ?? { enabled: false, config: null };
  } catch {
    return { enabled: false, config: null };
  }
};

export const useOfflineStore = create((set, get) => ({
  // 'ONLINE' | 'OFFLINE' | 'SYNCING'
  status: navigator.onLine ? "ONLINE" : "OFFLINE",
  serverEnabled: loadCfg().enabled, // /sync/status said ENABLED (persisted)
  device: loadDevice(), // { device_id, label, secret, seq }
  primed: false,
  priming: null, // { step, done, total } during first-run cache prime
  pending: 0, // outbox depth
  deadLetters: 0,
  lastSyncAt: null,
  lastPrimeAt: null,
  lastRefreshAt: null, // last successful background delta refresh
  conflictsOpen: 0,
  config: loadCfg().config, // server knobs from /sync/status (persisted)

  isOfflineCapable: () => {
    const s = get();
    return Boolean(s.serverEnabled && s.device && s.primed);
  },

  // Persist the server flag/config so it survives an offline reload.
  setServerConfig: (enabled, config) => {
    try {
      localStorage.setItem(LS_CFG, JSON.stringify({ enabled: Boolean(enabled), config: config ?? null }));
    } catch {
      /* private mode — store stays in-memory */
    }
    set({ serverEnabled: Boolean(enabled), config: config ?? get().config });
  },

  set: (patch) => set(patch),
  setStatus: (status) => set({ status }),
  setCounts: ({ pending, deadLetters }) =>
    set((s) => ({
      pending: pending ?? s.pending,
      deadLetters: deadLetters ?? s.deadLetters,
    })),
}));
